import styled, { createGlobalStyle, css, keyframes } from "styled-components";
import {
  ANIMATION,
  COLORS_THEME_WHITE,
  COLORS_GLOBAL,
  COLORS_WELCOME,
  FONTS,
  FONT_SIZES,
  SHADOWS,
  SPACING,
  TOUCH_TARGET,
} from "../../utils/constants";
import { BaseButton } from "../../components/Button";
import crimsonFont from "./assets/crimson-pro-latin.woff2";
import type {
  AnswerPanelStyleProps,
  EquationItemStyleProps,
  FigureStyleProps,
} from "./types";

export const PageRoot = styled.div`
  --welcome-ink: ${COLORS_THEME_WHITE.ternary};
  --welcome-muted: ${COLORS_WELCOME.muted};
  --welcome-accent: ${COLORS_THEME_WHITE.primary};
  --welcome-deep: ${COLORS_WELCOME.deep};
  --welcome-paper: ${COLORS_THEME_WHITE.backgroundBody};
  --welcome-surface: ${COLORS_THEME_WHITE.backgroundCard};
  --welcome-line: ${COLORS_THEME_WHITE.border};
  --welcome-soft: ${COLORS_WELCOME.soft};
  --welcome-negative: ${COLORS_GLOBAL.error};
  --welcome-on-deep: ${COLORS_WELCOME.onDeep};
  --welcome-text-sm: calc((${FONT_SIZES.base} + ${FONT_SIZES.md}) / 2);
  --welcome-text-md: ${FONT_SIZES.lg};
  --welcome-text-lg: calc((${FONT_SIZES.xl} + ${FONT_SIZES.xxl}) / 2);
  --welcome-heading: clamp(
    ${FONT_SIZES.xxl},
    3.2vw,
    calc(${FONT_SIZES.displayXl} + ${FONT_SIZES.md} / 4)
  );
  min-height: 100vh;
  min-width: 0;
  background: var(--welcome-paper);
  color: var(--welcome-ink);
  font-family: ${FONTS.serif};
  font-synthesis: none;
  font-variant-numeric: lining-nums tabular-nums;
  color-scheme: light;
  accent-color: var(--welcome-accent);
  caret-color: var(--welcome-accent);
  :where(h1, h2, h3, p) {
    margin: 0;
  }
  span {
    line-height: inherit;
  }
  h1,
  h2,
  h3 {
    text-wrap: balance;
  }
  :where(p) {
    line-height: 1.6;
  }
  button,
  a {
    -webkit-tap-highlight-color: transparent;
  }
  button {
    font-family: inherit;
  }
  a {
    text-underline-offset: 0.22em;
  }
  [id] {
    scroll-margin-top: ${SPACING.lg}px;
  }
  ::selection {
    background: var(--welcome-deep);
    color: var(--welcome-surface);
  }
  & :focus-visible {
    outline: 3px solid var(--welcome-accent);
    outline-offset: 5px;
    box-shadow: none;
  }
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation: none;
      transition: none;
      scroll-behavior: auto;
    }
  }
`;

const revealAnswer = keyframes`
  from { clip-path: inset(0 0 8% 0); }
  to { clip-path: inset(0); }
`;

export const Main = styled.main`
  max-width: 1320px;
  margin: auto;
  padding: 0 ${SPACING.xxl}px;
  @media (max-width: 700px) {
    padding: 0 ${SPACING.md}px;
  }
`;
export const Hero = styled.section`
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  align-items: center;
  gap: ${SPACING.xxl}px;
  padding: ${SPACING.xxl}px 0;
  @media (max-width: 950px) {
    grid-template-columns: 1fr;
    gap: ${SPACING.lg}px;
  }
  @media (max-width: 700px) {
    padding: ${SPACING.lg}px 0 ${SPACING.xl}px;
  }
`;
export const HeroCopy = styled.div`
  h1 {
    font-size: clamp(
      ${FONT_SIZES.displayLg},
      4.3vw,
      calc(${FONT_SIZES.displayXl} * 1.5)
    );
    line-height: 1.04;
    letter-spacing: -0.025em;
    font-weight: 600;
  }
  em {
    font-style: normal;
    color: var(--welcome-deep);
  }
`;
export const HeroDescription = styled.div`
  > p {
    max-width: 46ch;
    font-size: var(--welcome-text-lg);
    color: var(--welcome-muted);
    margin-bottom: ${SPACING.lg}px;
  }
  @media (max-width: 950px) {
    p {
      max-width: 65ch;
    }
  }
  @media (max-width: 700px) {
    p {
      font-size: var(--welcome-text-md);
      margin-bottom: ${SPACING.md}px;
    }
  }
`;
export const ManualNote = styled.small`
  display: block;
  margin-top: ${SPACING.md}px;
  color: var(--welcome-muted);
  font-size: var(--welcome-text-sm);
`;
export const Explorer = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.35fr);
  gap: ${SPACING.xxl}px;
  align-items: start;
  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    gap: ${SPACING.lg}px;
  }
`;
export const QuestionRail = styled.div`
  min-width: 0;
  padding: ${SPACING.md}px 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xl}px;
  @media (max-width: 850px) {
    padding: 0;
    gap: 0;
  }
`;
export const QuestionList = styled.div`
  display: flex;
  flex-direction: column;
  @media (max-width: 850px) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${SPACING.sm}px;
  }
`;
export const QuestionButton = styled(BaseButton)`
  width: 100%;
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  padding: ${SPACING.lg}px 0;
  border: 0;
  border-bottom: 1px solid var(--welcome-line);
  border-radius: 0;
  color: var(--welcome-muted);
  background: transparent;
  text-align: left;
  font-size: ${FONT_SIZES.xl};
  font-weight: 400;
  line-height: 1.25;
  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    opacity: 0;
  }
  &[aria-pressed="true"] {
    color: var(--welcome-deep);
    font-weight: 650;
    border-bottom-color: var(--welcome-deep);
  }
  &[aria-pressed="true"] svg {
    opacity: 1;
  }
  &:hover {
    color: var(--welcome-deep);
  }
  &:hover svg {
    opacity: 1;
  }
  &:focus-visible {
    outline-offset: 4px;
  }
  @media (max-width: 850px) {
    min-height: 68px;
    justify-content: center;
    text-align: center;
    font-size: var(--welcome-text-sm);
    padding: ${SPACING.sm}px ${SPACING.xs}px;
    border-bottom: 2px solid var(--welcome-line);
    &[aria-pressed="true"] {
      border-bottom-color: var(--welcome-deep);
    }
    svg {
      display: none;
    }
  }
`;
export const QuestionCopy = styled.div`
  h2 {
    font-size: ${FONT_SIZES.xxl};
    line-height: 1.2;
    font-weight: 550;
    margin-bottom: ${SPACING.sm}px;
  }
  p {
    color: var(--welcome-muted);
    font-size: var(--welcome-text-md);
    max-width: 40ch;
  }
  @media (max-width: 850px) {
    display: none;
  }
`;
export const Answer = styled.div`
  padding: ${SPACING.xl}px;
  min-width: 0;
  border-radius: 16px;
  background: var(--welcome-surface);
  box-shadow: ${SHADOWS.xlarge};
  @media (max-width: 700px) {
    padding: ${SPACING.lg}px ${SPACING.md}px;
  }
`;
export const AnswerPanels = styled.div`
  display: grid;
  align-items: start;
`;
export const AnswerPanel = styled.div<AnswerPanelStyleProps>`
  grid-area: 1 / 1;
  min-width: 0;
  visibility: ${({ $active }) => ($active ? "visible" : "hidden")};
  ${({ $active }) =>
    $active &&
    css`
      animation: ${revealAnswer} 240ms cubic-bezier(0.16, 1, 0.3, 1);
    `}
  @media (max-width: 850px) {
    display: ${({ $active }) => ($active ? "block" : "none")};
  }
`;
export const SampleCaption = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${SPACING.sm}px;
  font-size: var(--welcome-text-sm);
  color: var(--welcome-muted);
  padding-bottom: ${SPACING.lg}px;
  span:first-child {
    color: var(--welcome-deep);
  }
`;
export const AnswerHeading = styled.h2`
  font-size: ${FONT_SIZES.xl};
  font-weight: 550;
  line-height: 1.25;
`;
export const AnswerValue = styled.p`
  font-size: clamp(
    ${FONT_SIZES.displayLg},
    3.6vw,
    calc(${FONT_SIZES.xxxl} * 2)
  );
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin-top: ${SPACING.md}px;
  span {
    font-size: var(--welcome-text-md);
    color: var(--welcome-muted);
    font-weight: 400;
    letter-spacing: 0;
    display: inline-block;
  }
  @media (max-width: 440px) {
    span {
      display: block;
      margin-top: ${SPACING.xs}px;
    }
  }
`;
export const Explanation = styled.p`
  color: var(--welcome-muted);
  font-size: var(--welcome-text-sm);
  margin-top: ${SPACING.sm}px;
  &.goal-remainder {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: ${SPACING.sm}px;
  }
  strong {
    color: var(--welcome-deep);
    font-weight: 550;
  }
`;
export const Comparison = styled.p`
  margin-top: ${SPACING.sm}px;
  color: var(--welcome-deep);
  font-size: var(--welcome-text-sm);
  font-weight: 550;
`;
export const BalanceEquation = styled.dl`
  margin: ${SPACING.lg}px 0 0;
  display: grid;
  gap: ${SPACING.md}px;
`;
export const EquationItem = styled.div<EquationItemStyleProps>`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: baseline;
  gap: ${SPACING.sm}px ${SPACING.md}px;
  font-size: var(--welcome-text-md);
  ${({ $total }) => $total && `border-top: 1px solid var(--welcome-line); padding-top: ${SPACING.md}px; font-weight: 600;`}
  dt {
    color: ${({ $debt }) => ($debt ? "var(--welcome-negative)" : "var(--welcome-ink)")};
  }
  dd {
    margin: 0;
    white-space: nowrap;
  }
`;
export const Figure = styled.span<FigureStyleProps>`
  grid-column: 1 / -1;
  width: ${({ $width }) => $width}%;
  height: 8px;
  border-radius: 2px;
  background: ${({ $debt }) => ($debt ? "var(--welcome-negative)" : "var(--welcome-accent)")};
`;
export const BalanceDetails = styled.details`
  border-top: 1px solid var(--welcome-line);
  margin-top: ${SPACING.lg}px;
  summary {
    cursor: pointer;
    min-height: 44px;
    padding-top: ${SPACING.md}px;
    color: var(--welcome-deep);
    font-size: var(--welcome-text-sm);
  }
  summary:hover {
    text-decoration: underline;
  }
`;
export const DataRows = styled.dl`
  margin: ${SPACING.sm}px 0 0;
  font-size: var(--welcome-text-sm);
  display: grid;
  gap: ${SPACING.sm}px;
  div {
    display: flex;
    justify-content: space-between;
    gap: ${SPACING.md}px;
  }
  dd {
    margin: 0;
    white-space: nowrap;
  }
  .subtotal,
  .total {
    border-top: 1px solid var(--welcome-line);
    padding-top: ${SPACING.sm}px;
    font-weight: 600;
  }
  .debt {
    color: var(--welcome-negative);
  }
`;
export const ControlRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${SPACING.sm}px;
`;
export const SelectorGroup = styled.div`
  display: flex;
  gap: ${SPACING.xs}px;
`;
export const Selector = styled(BaseButton)`
  min-height: 44px;
  padding: ${SPACING.sm}px ${SPACING.md}px;
  border: 1px solid transparent;
  border-radius: 6px;
  color: var(--welcome-muted);
  background: transparent;
  font-size: var(--welcome-text-sm);
  line-height: 1.2;
  &[aria-pressed="true"] {
    color: var(--welcome-deep);
    background: var(--welcome-soft);
    font-weight: 600;
    border-color: var(--welcome-deep);
  }
  &:hover {
    color: var(--welcome-deep);
    background: var(--welcome-soft);
  }
`;
export const MoneySummary = styled.dl`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${SPACING.lg}px;
  margin: ${SPACING.lg}px 0;
  padding: ${SPACING.md}px 0;
  border-top: 1px solid var(--welcome-line);
  border-bottom: 1px solid var(--welcome-line);
  div {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: ${SPACING.xs}px ${SPACING.md}px;
  }
  dt {
    color: var(--welcome-muted);
    font-size: var(--welcome-text-sm);
  }
  dd {
    margin: 0;
    font-weight: 600;
  }
`;
export const CategoryRows = styled.dl`
  display: grid;
  gap: ${SPACING.sm}px;
  margin: 0;
  font-size: var(--welcome-text-sm);
  div {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: ${SPACING.xs}px ${SPACING.md}px;
  }
  dd {
    margin: 0;
  }
  span {
    grid-column: 1 / -1;
    height: 4px;
    background: var(--welcome-paper);
  }
`;
export const CategoryFill = styled.i<FigureStyleProps>`
  display: block;
  width: ${({ $width }) => $width}%;
  height: 100%;
  background: var(--welcome-accent);
`;
export const GoalSelectors = styled(SelectorGroup)`
  flex-wrap: wrap;
  margin-top: ${SPACING.md}px;
`;
export const GoalDetail = styled.div`
  margin-top: ${SPACING.lg}px;
  h3 {
    font-size: var(--welcome-text-lg);
    font-weight: 600;
  }
  p {
    color: var(--welcome-muted);
    font-size: var(--welcome-text-sm);
    margin-top: ${SPACING.xs}px;
  }
`;
export const GoalProgress = styled.progress`
  display: block;
  appearance: none;
  width: 100%;
  height: 12px;
  border: 0;
  border-radius: 2px;
  overflow: hidden;
  margin-top: ${SPACING.lg}px;
  background: var(--welcome-paper);
  color: var(--welcome-accent);
  &::-webkit-progress-bar {
    background: var(--welcome-paper);
  }
  &::-webkit-progress-value {
    background: var(--welcome-accent);
  }
  &::-moz-progress-bar {
    background: var(--welcome-accent);
  }
`;
export const SmallNote = styled.p`
  font-size: var(--welcome-text-sm);
  color: var(--welcome-muted);
  border-top: 1px solid var(--welcome-line);
  padding-top: ${SPACING.md}px;
  margin-top: ${SPACING.lg}px;
  strong {
    color: var(--welcome-ink);
  }
`;
export const Setup = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${SPACING.xxl * 2}px;
  padding: ${SPACING.xxl * 2}px 0;
  align-items: start;
  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    gap: ${SPACING.xl}px;
    padding: ${SPACING.xxl}px 0;
  }
`;
export const SetupCopy = styled.div`
  h2 {
    font-size: var(--welcome-heading);
    line-height: 1.1;
    font-weight: 550;
    max-width: 19ch;
  }
  p {
    font-size: var(--welcome-text-md);
    color: var(--welcome-muted);
    margin-top: ${SPACING.lg}px;
    max-width: 48ch;
  }
  a {
    display: inline-flex;
    align-items: center;
    gap: ${SPACING.sm}px;
    min-height: 44px;
    margin-top: ${SPACING.lg}px;
    color: var(--welcome-deep);
    font-size: var(--welcome-text-md);
  }
`;
export const SetupSteps = styled.ol`
  list-style: decimal;
  padding: 0 0 0 ${SPACING.lg}px;
  margin: 0;
  li {
    padding: ${SPACING.lg}px 0 ${SPACING.lg}px ${SPACING.sm}px;
    border-top: 1px solid var(--welcome-line);
  }
  li:first-child {
    padding-top: 0;
    border-top: 0;
  }
  li:last-child {
    padding-bottom: 0;
  }
  li::marker {
    color: var(--welcome-accent);
    font-size: var(--welcome-text-lg);
  }
  h3 {
    font-size: var(--welcome-text-lg);
    font-weight: 600;
  }
  p {
    font-size: var(--welcome-text-md);
    color: var(--welcome-muted);
    margin-top: ${SPACING.sm}px;
  }
`;
export const Closing = styled.section`
  padding: ${SPACING.xl}px ${SPACING.xxl}px;
  background: var(--welcome-deep);
  color: var(--welcome-paper);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.xxl}px;
  border-radius: 12px;
  margin-bottom: ${SPACING.xxl}px;
  h2 {
    font-size: clamp(${FONT_SIZES.xxxl}, 2.5vw, ${FONT_SIZES.displayLg});
    line-height: 1.1;
    font-weight: 550;
  }
  p {
    font-size: var(--welcome-text-md);
    color: var(--welcome-on-deep);
    margin-top: ${SPACING.md}px;
    max-width: 60ch;
  }
  @media (max-width: 850px) {
    align-items: flex-start;
    flex-direction: column;
    gap: ${SPACING.lg}px;
  }
  @media (max-width: 700px) {
    padding: ${SPACING.xl}px ${SPACING.lg}px;
  }
`;
export const ClosingAction = styled(BaseButton)`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: ${SPACING.sm}px;
  flex-shrink: 0;
  min-height: 48px;
  padding: ${SPACING.sm}px ${SPACING.lg}px;
  border-radius: 8px;
  color: var(--welcome-deep);
  background: var(--welcome-paper);
  text-decoration: none;
  font-size: var(--welcome-text-md);
  font-weight: 600;
  &:hover {
    background: var(--welcome-surface);
  }
  &:focus-visible {
    outline-color: var(--welcome-paper);
  }
`;
export const SrOnly = styled.p`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -${SPACING.xxs / 2}px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;

export const WelcomeScrollReset = createGlobalStyle`
  @font-face {
    font-family: 'Crimson Pro';
    src: url(${crimsonFont}) format('woff2');
    font-weight: 400 700;
    font-style: normal;
    font-display: swap;
  }
  html:root, html:root body, html:root #root { height: auto; min-height: 100%; }
  html:root body { overflow-y: visible !important; }
`;
export const SkipLink = styled.a`
  position: fixed;
  top: 0;
  left: ${SPACING.md}px;
  z-index: 100;
  padding: ${SPACING.md}px;
  background: var(--welcome-surface);
  color: var(--welcome-deep);
  transform: translateY(-150%);
  &:focus {
    transform: translateY(0);
  }
`;
export const HeaderBar = styled.header`
  max-width: 1320px;
  margin: auto;
  padding: ${SPACING.lg}px ${SPACING.xxl}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.lg}px;
  @media (max-width: 700px) {
    padding: ${SPACING.lg}px ${SPACING.md}px;
    gap: ${SPACING.sm}px;
  }
`;
export const Brand = styled.a`
  display: inline-flex;
  gap: ${SPACING.sm}px;
  align-items: center;
  text-decoration: none;
  color: inherit;
  font-size: var(--welcome-text-lg);
  font-weight: 650;
  line-height: 1.15;
  span {
    line-height: inherit;
  }
  @media (max-width: 440px) {
    font-size: var(--welcome-text-md);
    max-width: 160px;
  }
`;
export const BrandLogo = styled.img`
  width: 34px;
  height: 34px;
  object-fit: contain;
`;
export const HeaderActions = styled.nav`
  display: flex;
  align-items: center;
  gap: ${SPACING.lg}px;
  font-size: var(--welcome-text-sm);
  > a {
    color: inherit;
    text-decoration: none;
  }
  > a:hover {
    text-decoration: underline;
  }
  @media (max-width: 950px) {
    .section-link {
      display: none;
    }
  }
  @media (max-width: 440px) {
    gap: ${SPACING.md}px;
    .signin-link {
      display: none;
    }
  }
`;
export const ActionLink = styled(BaseButton)`
  justify-content: center;
  min-height: ${TOUCH_TARGET + SPACING.xxs}px;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.sm + SPACING.xs}px ${SPACING.lg}px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: var(--welcome-deep);
  && {
    color: var(--welcome-surface);
  }
  font-size: var(--welcome-text-md);
  font-weight: 600;
  line-height: 1.2;
  text-decoration: none;
  transition:
    background ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOutExpo},
    transform ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOutExpo};
  svg {
    width: 20px;
    height: 20px;
  }
  &:hover {
    background: var(--welcome-accent);
    transform: translateY(-1px);
    text-decoration: none;
  }
`;
export const Footer = styled.footer`
  max-width: 1320px;
  margin: auto;
  padding: ${SPACING.xl}px ${SPACING.xxl}px;
  border-top: 1px solid var(--welcome-line);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${SPACING.lg}px;
  font-size: var(--welcome-text-sm);
  color: var(--welcome-muted);
  nav {
    display: flex;
    align-items: center;
    gap: ${SPACING.lg}px;
    flex-wrap: wrap;
  }
  a {
    color: inherit;
  }
  a:hover {
    color: var(--welcome-deep);
  }
  @media (max-width: 700px) {
    padding: ${SPACING.lg}px ${SPACING.md}px;
    flex-direction: column;
    align-items: flex-start;
  }
`;
